import React, { memo, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { Box, DrawerBody, HStack, Text, VStack } from "@chakra-ui/react"
import Map, { Marker } from "react-map-gl"
import workerClass from "worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker"
import "mapbox-gl/dist/mapbox-gl.css"
import mapboxgl from "mapbox-gl"

import { useConfigContext } from "@app/providers/config"
import Icon from "@app/components/Icon"
import Link from "@app/components/Link"

import type { Store } from "@app/hooks/useFindInStore"

// @ts-ignore
mapboxgl.workerClass = workerClass

type Props = {
  store: Store | undefined | false
}

export const ProductStockOverlayFormDetails: React.FC<Props> = memo(({ store }) => {
  const { product: locales } = useStaticQuery<GatsbyTypes.ProductStockOverlayFormDetailsQuery>(graphql`
    query ProductStockOverlayFormDetails {
      product: sanityTemplateProduct {
        additionalStockDirections
      }
    }
  `)
  const {
    store: { mapboxAccessToken },
  } = useConfigContext()

  const [mapState, setMapState] = useState({
    latitude: store?.location?.lat || 100,
    longitude: store?.location?.lng || 100,
    zoom: 13,
  })

  return store ? (
    <DrawerBody as={VStack} alignItems="stretch" justifyContent="flex-start" spacing={16} px={0} pb={0}>
      <VStack flexShrink={0} alignItems="stretch" justifyContent="flex-start" spacing={6} px={12} py={0}>
        <Text variant="text20">{store.title}</Text>
        <HStack alignItems="flex-start" justifyContent="flex-start" spacing={8}>
          <Icon name="location" width={5} />
          <VStack alignItems="flex-start" justifyContent="flex-start" spacing={2}>
            <Text variant="paragraph16" my={0} lineHeight="22px" dangerouslySetInnerHTML={{ __html: store.addressText }} />
            {store.location?.lat && store.location?.lng && (
              <Text
                variant="paragraph16"
                as={Link}
                external
                flexShrink={0}
                color="brand.blue"
                title={locales?.additionalStockDirections || ""}
                aria-label={locales?.additionalStockDirections || ""}
                to={`//www.google.com/maps/search/${store.title}`}
              >
                {locales?.additionalStockDirections}
              </Text>
            )}
          </VStack>
        </HStack>
        {store.phone && (
          <HStack alignItems="flex-start" justifyContent="flex-start" spacing={8}>
            <Icon name="call" width={5} />
            <Text as={Link} to={`tel:${store.phone}`} variant="paragraph16" my={0} lineHeight="22px">
              {store.phone}
            </Text>
          </HStack>
        )}
        {store.openhours?.length && (
          <HStack alignItems="flex-start" justifyContent="flex-start" spacing={8}>
            <Icon name="time" width={5} />
            <VStack alignItems="stretch" justifyContent="flex-start" spacing={3} flex={1}>
              {store.openhours?.map(({ day, hours }) => (
                <HStack key={day.toString()} alignItems="center" justifyContent="space-between">
                  <Text variant="paragraph16" flexShrink={0} my={0} w="50px">
                    {day.slice(0, day.toLowerCase() === "thursday" ? 4 : 3)}
                    {day.slice(0, 1).toLowerCase() === "t" ? "s" : ""}
                  </Text>
                  {hours && (
                    <Text variant="paragraph16" flex={1} my={0}>
                      {hours}
                    </Text>
                  )}
                </HStack>
              ))}
            </VStack>
          </HStack>
        )}
      </VStack>
      {store.location?.lat && store.location?.lng && (
        <Box flex={1} minHeight="250px">
          <Map
            {...mapState}
            style={{ width: "100%", height: "100%" }}
            mapStyle="mapbox://styles/mapbox/light-v10"
            mapboxAccessToken={mapboxAccessToken}
            onMove={evt => setMapState(evt?.viewState)}
          >
            <Marker latitude={store.location.lat} longitude={store.location.lng} anchor="bottom">
              <Icon name="pin" width={8} />
            </Marker>
          </Map>
        </Box>
      )}
    </DrawerBody>
  ) : null
})
